import { createContext, useContext, useEffect, useState } from "react"
import { type StoreApi, createStore, useStore } from "zustand"
import { devtools, subscribeWithSelector } from "zustand/middleware"

/**
 * Shape of the shared form store across all steps.
 * `data` is the live accumulated state; `defaultData` is the server-provided
 * initial values used to pre-populate fields on first load.
 */
export interface IMultiStepFormStore<T> {
  defaultData: Partial<T>
  data: Partial<T>
  updateForm: (data: Partial<T>) => void
  getDefaultValues: <K extends keyof T>(keys: K[]) => Pick<Partial<T>, K>
}

/**
 * Typed surface of the subscribeWithSelector middleware.
 * Cast needed because the Context is typed as the base StoreApi,
 * which doesn't expose the selector overload of subscribe.
 */
interface SubscribeWithSelector<T> {
  subscribe: <S>(
    selector: (state: T) => S,
    listener: (selectedState: S, previousSelectedState: S) => void,
    options?: {
      equalityFn?: (a: S, b: S) => boolean
      fireImmediately?: boolean
    },
  ) => () => void
}

/**
 * Factory that creates a self-contained multi-step form context.
 * Each call produces an isolated Provider, hook, and subscriber triple —
 * allowing multiple independent form flows to coexist without shared state.
 */
export const createMultiStepFormContext = <T,>() => {
  const Context = createContext<StoreApi<IMultiStepFormStore<T>> | null>(null)

  const Provider = ({
    children,
    defaultData = {} as Partial<T>,
  }: React.PropsWithChildren<{ defaultData?: Partial<T> }>) => {
    const [store] = useState(() =>
      createStore<IMultiStepFormStore<T>>()(
        devtools(
          subscribeWithSelector((set) => ({
            defaultData,
            data: defaultData,
            updateForm: (newData) =>
              set((state) => ({
                data: { ...state.data, ...newData },
              })),
            getDefaultValues: (keys) => {
              return Object.fromEntries(
                keys
                  .filter((key) => key in defaultData)
                  .map((key) => [key, defaultData[key]]),
              ) as Pick<Partial<T>, (typeof keys)[number]>
            },
          })),
          { name: "Form" },
        ),
      ),
    )

    return <Context.Provider value={store}>{children}</Context.Provider>
  }

  /**
   * Select a slice of the form store for rendering.
   * Equivalent to Zustand's useStore with a selector.
   */
  const useMultiStepForm = <U,>(
    selector: (state: IMultiStepFormStore<T>) => U,
  ) => {
    const store = useContext(Context)

    if (store === null) {
      throw new Error("useMultiStepForm must be used within its Provider")
    }

    return useStore(store, selector)
  }

  /**
   * Subscribe to a slice of the form store outside of the render cycle.
   * Useful for side effects that should react to form state changes
   * without causing re-renders — e.g. feeding an autosave stream.
   */
  const useMultiStepFormSubscribe = <S,>(
    selector: (state: IMultiStepFormStore<T>) => S,
    listener: (selectedState: S, previousSelectedState: S) => void,
    options?: {
      equalityFn?: (a: S, b: S) => boolean
      fireImmediately?: boolean
    },
  ) => {
    const store = useContext(Context)

    if (store === null) {
      throw new Error(
        "useMultiStepFormSubscribe must be used within its Provider",
      )
    }

    useEffect(() => {
      return (
        store as unknown as SubscribeWithSelector<IMultiStepFormStore<T>>
      ).subscribe(selector, listener, options)
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])
  }

  return { Provider, useMultiStepForm, useMultiStepFormSubscribe }
}
